import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import Moment from 'react-moment';

import XMTable from '../../components/XMTable';
import XMSearchBox from '../../components/XMSearchBox';
import XMPagination from '../../components/XMPagination';
import { AppCell, NoResults } from './rowParts';
import { dash } from './advertModel';
import { INSTALLED_SITES_SCOPES } from './installedSitesScope';

const HEADERS = [
  { name: 'STORE' },
  { name: 'INSTALLED BY' },
  { name: 'VERSION', style: { width: '9rem' } },
  { name: 'INSTALLED', style: { width: '11rem' } }
];

const PAGE_SIZE = 20;

/**
 * Installed sites for one catalog app (All Apps' count) or one advert (Manage App Adverts' count) —
 * UN-3248 AC7, scoped per tab since UN-3252 (see installedSitesScope). Rendered as a modal over the
 * Manage Apps shell; the route exists so the drill-down is linkable and the browser Back button closes it.
 *
 * Unlike the list tabs this is server-paged and server-searched: the ledger replica can hold every
 * store in the fleet, so there is no full list to filter client-side.
 */
const InstalledSites = ({ scope = 'app' }) => {
  const { subject: subjectOf, load, loadSites, backTo, label, none } = INSTALLED_SITES_SCOPES[scope];
  const { uuid } = useParams();
  const dispatch = useDispatch();
  // Only what this scope reads: the app modal no longer re-renders on advert-slice changes, or the reverse.
  const subject = useSelector(state => subjectOf(state.appStore, uuid));
  const sites = useSelector(state => state.appStore.sites);
  const sitesLoaded = useSelector(state => state.appStore.sitesLoaded);
  const sitesError = useSelector(state => state.appStore.sitesError);
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState('');
  // The unfiltered install count, kept across searches: the title states how many stores the app is
  // installed on, which a search must not rewrite ("installed on 1 stores" while filtering was the
  // first thing to look wrong on screen). The searched count belongs to the table, not the title.
  const [installedTotal, setInstalledTotal] = useState(null);

  const haveSubject = subject !== null;
  useEffect(() => {
    if (!haveSubject) dispatch(load(uuid)).catch(() => {});
  }, [dispatch, load, uuid, haveSubject]);

  useEffect(() => {
    setInstalledTotal(null);
  }, [uuid]);

  useEffect(() => {
    // Page 0 on every new search term: staying on page 4 of the previous result set would show an
    // empty page and no way to tell whether the search matched nothing. `current` keeps a superseded
    // response out of the title total (review on #282); the reducer keeps it out of the table (requestId).
    let current = true;
    dispatch(loadSites(uuid, { search, page: 0, size: PAGE_SIZE }))
      .then(sitesPage => {
        if (current && !search) setInstalledTotal(sitesPage.totalElements);
      })
      .catch(() => {});
    return () => {
      current = false;
    };
  }, [dispatch, loadSites, uuid, search]);

  const paginate = page => dispatch(loadSites(uuid, { search, page, size: PAGE_SIZE })).catch(() => {});

  const close = () => {
    // A fresh browser entry (a pasted link) has no history to go back to, so Back would leave the app.
    if (location.key && location.key !== 'default') {
      navigate(-1);
    } else {
      navigate(backTo);
    }
  };

  const name = subject ? subject.name : label;
  // The unfiltered page total is authoritative; the subject's own installCount — the same set — stands in
  // until the first page lands (and while a search is in flight before any unfiltered load).
  const total = installedTotal ?? (subject ? subject.installCount : null);
  const missing = total === null || total === undefined;

  return (
    <Modal isOpen size="lg" toggle={close} scrollable>
      <ModalHeader toggle={close}>
        {name} · installed on {missing ? '—' : total} {total === 1 ? 'store' : 'stores'}
      </ModalHeader>
      <ModalBody>
        <Row className="mb-3">
          <Col md="7">
            <XMSearchBox onSearch={({ fuzzyString }) => setSearch(fuzzyString || '')} />
          </Col>
          {search && sitesLoaded ? (
            <Col className="text-end text-muted align-self-center">
              {sites.totalElements} of {missing ? '—' : total} matching &ldquo;{search}&rdquo;
            </Col>
          ) : null}
        </Row>

        {sitesError ? <Alert color="danger">{sitesError}</Alert> : null}

        <XMTable headers={HEADERS}>
          {!sitesLoaded && !sitesError ? (
            <NoResults colSpan={HEADERS.length}>Loading installed sites&hellip;</NoResults>
          ) : null}
          {sitesLoaded && !sitesError && sites.content.length === 0 ? (
            <NoResults colSpan={HEADERS.length}>
              {search ? `No installed store matches “${search}”.` : none}
            </NoResults>
          ) : null}
          {sites.content.map(site => (
            <tr key={site.storeId}>
              <td className="align-middle">
                {/* Name and domain are both null when the store directory cannot resolve the id (a
                    store deleted since it installed, or one from another region's directory), so the
                    id becomes the primary label — and the subtitle is dropped rather than repeating
                    it under itself. */}
                <AppCell
                  name={site.storeName || site.storeId}
                  subtitle={site.storeName ? site.storeDomain || site.storeId : site.storeDomain}
                />
              </td>
              {/* installedBy is the server's three-way attribution: a requester email, the literal
                  "system" for system-published installs (CQ-2), or null on backfilled rows. */}
              <td className="align-middle">{dash(site.installedBy)}</td>
              <td className="align-middle">{dash(site.installedVersion)}</td>
              <td className="align-middle">
                {site.installedDate ? <Moment format="YYYY-MM-DD HH:mm">{site.installedDate}</Moment> : dash(null)}
              </td>
            </tr>
          ))}
        </XMTable>

        <XMPagination pagination={sites} paginate={paginate} />
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={close}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default InstalledSites;
